/**
 * Supplemental applications, as a form library.
 *
 * The problem this solves is mundane and constant: people find supplementals by
 * Googling, and land on dead links or superseded forms. Every entry here has been
 * fetched, parsed, and checked to be a real form for that class — a link that
 * 404s mid-quote is worse than no link at all.
 *
 * Any carrier's supplemental is usually sufficient to gather the information, so
 * the list leads with the most complete form and keeps the rest as fallbacks
 * rather than pretending there is one right answer per class.
 *
 * ⚠️ THE ROW IS A FORM, NOT A CLASS (Josh's mock, 2026-09-04). It used to be an
 * accordion of classes: to find "loss history questionnaire" you had to already
 * know which class it lived under, and the same carrier PDF appeared under six
 * classes as six separate rows with no sign they were the same document. Rows
 * are forms now, deduplicated by URL, with the classes they cover as a column —
 * so searching for the form finds the form.
 *
 * ⚠️ NO "LAST USED" COLUMN, AND NO "RECENTLY USED" CARD. The mock has both. We
 * record no usage of these links — they are anchors to carrier sites — so every
 * value in that column would have been invented, and a made-up date on a form
 * library is exactly the kind of number someone would later make a decision
 * with. The column shows when the link was last verified, which we do know and
 * which is the thing that actually goes stale.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { StatCard } from '../components/StatCard';
import { api, type FormSummary } from '../lib/api';
import catalog from '../data/supplementals.json';

interface FormRow {
  title: string;
  url: string;
  chars: number;
  checkedAt: string;
}

const BY_CLASS = catalog.byClass as Record<string, FormRow[]>;
const CLASSES = Object.keys(BY_CLASS).sort();

/**
 * What kind of form this is, read off its own title.
 *
 * ⚠️ DERIVED, NOT STORED — and that is why the fallback is the honest one. The
 * catalogue carries no category field, so anything here is inference from the
 * carrier's own wording. Where the wording says nothing, the tag says "Form"
 * rather than guessing "Supplemental", because a wrong category on a form
 * library sends someone to the wrong document with confidence.
 */
type Kind = 'online' | 'application' | 'questionnaire' | 'endorsement' | 'supplemental' | 'form';

const KIND_LABEL: Record<Kind, string> = {
  online: 'Online form',
  application: 'Application',
  questionnaire: 'Questionnaire',
  endorsement: 'Endorsement',
  supplemental: 'Supplemental',
  form: 'Form',
};

function kindOf(title: string): Kind {
  const t = title.toLowerCase();
  if (t.includes('questionnaire')) return 'questionnaire';
  if (t.includes('endorsement')) return 'endorsement';
  if (t.includes('acord') || t.includes('application')) return 'application';
  if (t.includes('supplement')) return 'supplemental';
  return 'form';
}

/** The tile glyph. One per kind so the left edge is scannable. */
function KindIcon({ kind }: { kind: Kind }): JSX.Element {
  const paths: Record<Kind, string> = {
    online: 'M12 3a9 9 0 100 18 9 9 0 000-18zM8 12l3 3 5-5',
    application: 'M6 3h9l4 4v14H6zM14 3v5h5M9 12h7M9 16h5',
    questionnaire: 'M9 9a3 3 0 114 2.8c-.7.3-1 .9-1 1.7v.5M12 17.5h.01M12 3a9 9 0 100 18 9 9 0 000-18z',
    endorsement: 'M4 7h16M4 12h10M4 17h7M17 15l2 2 4-4',
    supplemental: 'M6 3h9l4 4v14H6zM14 3v5h5M12 11v6M9 14h6',
    form: 'M6 3h9l4 4v14H6zM14 3v5h5',
  };
  return (
    <span className={`sa-ic is-${kind}`} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d={paths[kind]} />
      </svg>
    </span>
  );
}

/** A row in the library: one form, and every class it covers. */
interface LibraryRow {
  id: string;
  title: string;
  note: string;
  kind: Kind;
  classes: string[];
  checkedAt: string;
  /** Set on web questionnaires — the class whose form to open. */
  webClass?: string;
  /** Set on carrier PDFs. */
  url?: string;
  reviewed?: boolean;
}

export function Supplementals() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'all' | Kind>('all');
  const [cls, setCls] = useState('all');

  /**
   * Which classes have a web questionnaire rather than only the carrier's PDF.
   *
   * Fetched rather than bundled, because whether a form is member-visible
   * depends on review state, which lives on the server. A failed fetch just
   * means everything shows as PDFs — the behaviour that shipped — so there is
   * nothing to handle.
   */
  const [webForms, setWebForms] = useState<Map<string, FormSummary>>(new Map());
  /**
   * ⚠️ ITS OWN FLAG, NOT `webForms.size === 0`. An org that genuinely holds no
   * web questionnaires would then shimmer forever, waiting for something that
   * is never coming.
   */
  const [formsLoading, setFormsLoading] = useState(true);
  useEffect(() => {
    api
      .listForms()
      .then((r) => setWebForms(new Map(r.forms.map((f) => [f.class, f]))))
      .catch(() => undefined)
      .finally(() => setFormsLoading(false));
  }, []);

  /**
   * One row per form.
   *
   * ⚠️ CARRIER PDFS ARE KEYED BY URL, NOT BY TITLE. The same document is listed
   * under several classes and the titles differ slightly between them; keying on
   * the title would leave near-duplicate rows for one form, which is the mess
   * the class accordion already had.
   */
  const rows = useMemo<LibraryRow[]>(() => {
    const web: LibraryRow[] = [...webForms.entries()]
      .filter(([c]) => c in BY_CLASS)
      .map(([c, f]) => ({
        id: `web:${c}`,
        title: `${c} questionnaire`,
        note: f.audience.insured
          ? `${f.fields} questions · ${f.audience.insured} only the insured can answer`
          : `${f.fields} questions, fillable here`,
        kind: 'online' as Kind,
        classes: [c],
        checkedAt: '',
        webClass: c,
        reviewed: f.reviewed,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));

    const byUrl = new Map<string, LibraryRow>();
    for (const c of CLASSES) {
      for (const f of BY_CLASS[c]!) {
        const held = byUrl.get(f.url);
        if (held) {
          if (!held.classes.includes(c)) held.classes.push(c);
          continue;
        }
        byUrl.set(f.url, {
          id: f.url,
          title: f.title,
          note: 'Hosted by the carrier',
          kind: kindOf(f.title),
          classes: [c],
          checkedAt: f.checkedAt,
          url: f.url,
        });
      }
    }
    const pdfs = [...byUrl.values()].sort((a, b) => a.title.localeCompare(b.title));

    // Online first: it is the thing that replaces printing a PDF and typing
    // into it, and it is what this page exists to move people towards.
    return [...web, ...pdfs];
  }, [webForms]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (kind === 'all' || r.kind === kind) &&
        (cls === 'all' || r.classes.includes(cls)) &&
        (!needle ||
          r.title.toLowerCase().includes(needle) ||
          r.classes.some((c) => c.toLowerCase().includes(needle))),
    );
  }, [rows, q, kind, cls]);

  const kinds = useMemo(() => {
    const seen = new Set(rows.map((r) => r.kind));
    return (Object.keys(KIND_LABEL) as Kind[]).filter((k) => seen.has(k));
  }, [rows]);

  const filtered = q.trim() !== '' || kind !== 'all' || cls !== 'all';
  const clear = () => {
    setQ('');
    setKind('all');
    setCls('all');
  };

  const onlineCount = rows.filter((r) => r.kind === 'online').length;

  return (
    <div className="wrap">
      <PageHero
        title="Supplemental Applications"
        sub="Verified carrier forms and questionnaires for the classes we write — every link checked, so none of them 404 mid-quote."
        action={
          <button className="btn btn-primary" onClick={() => document.getElementById('sa-search')?.focus()}>
            Browse the form library
          </button>
        }
      />

      <div className="stat-row">
        {/* ⚠️ THESE TWO ARE NOT LOADING, AND SAYING SO MATTERS. The catalogue is
            bundled with the build, so the form and class counts are correct on
            the first paint. Skeletoning them to match the third card would be
            inventing a wait that does not exist. */}
        <StatCard
          tone="blue"
          icon="M6 3h9l4 4v14H6zM14 3v5h5M9 12h7M9 16h5"
          label="Available forms"
          value={rows.length}
          note="Forms and questionnaires"
        />
        <StatCard
          tone="violet"
          icon="M12 3l9 5-9 5-9-5zM3 13l9 5 9-5M3 17l9 5 9-5"
          label="Classes covered"
          value={CLASSES.length}
          note="Commercial classes"
        />
        {/*
          ⚠️ NOT THE MOCK'S "RECENTLY USED". We hold no usage data on these — they
          are links to carrier sites — so that card could only have shown an
          invented number. This one is real, and it is the number that matters:
          how much of the library you can fill in here instead of printing.

          It waits on /api/forms, so it is the one card here that skeletons.
        */}
        <StatCard
          tone="green"
          icon="M12 3a9 9 0 100 18 9 9 0 000-18zM8 12l3 3 5-5"
          label="Fillable online"
          value={onlineCount}
          note={onlineCount ? 'No printing, no typing it back in' : 'Carrier PDFs only, for now'}
          loading={formsLoading}
        />
      </div>

      <section className="panel lr-table-panel">
        <div className="lr-toolbar">
          <div className="lr-search">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" />
            </svg>
            <input
              id="sa-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search supplemental applications…"
              aria-label="Search supplemental applications"
            />
          </div>

          <div className="lr-filters">
            <select value={kind} onChange={(e) => setKind(e.target.value as 'all' | Kind)} aria-label="Category">
              <option value="all">All categories</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
            <select value={cls} onChange={(e) => setCls(e.target.value)} aria-label="Class">
              <option value="all">All classes</option>
              {CLASSES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <button className="lr-clear" type="button" disabled={!filtered} onClick={clear}>
              Clear filters
            </button>
          </div>
        </div>

        {!visible.length ? (
          <p className="lr-empty">
            Nothing matches that.
            <span>Try a different class, or clear the filters.</span>
          </p>
        ) : (
          <>
            <table className="lr-table">
              <thead>
                <tr>
                  <th>Form name</th>
                  <th>Category</th>
                  <th className="sa-hide-md">Classes</th>
                  <th className="sa-hide-md">Link checked</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="lr-cell">
                        <KindIcon kind={r.kind} />
                        <div>
                          <b>{r.title}</b>
                          <span>
                            {r.note}
                            {r.reviewed === false && ' · draft, AAS review pending'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`tag is-${r.kind}`}>{KIND_LABEL[r.kind]}</span>
                    </td>
                    <td className="sa-hide-md">
                      <div className="sa-classes">
                        {r.classes.length > 3
                          ? `${r.classes.slice(0, 3).join(', ')} +${r.classes.length - 3} more`
                          : r.classes.join(', ')}
                      </div>
                    </td>
                    {/* Not "last used" — see the file header. */}
                    <td className="sa-hide-md">
                      <span className="sa-when">{r.checkedAt || '—'}</span>
                    </td>
                    <td className="sa-act">
                      {r.webClass ? (
                        <>
                          <button
                            className="btn btn-ghost"
                            onClick={() =>
                              navigate(`/hub/supplementals/${encodeURIComponent(r.webClass!)}?send=1`)
                            }
                          >
                            Send to insured
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={() => navigate(`/hub/supplementals/${encodeURIComponent(r.webClass!)}`)}
                          >
                            Fill it out
                          </button>
                        </>
                      ) : (
                        <a className="btn btn-ghost" href={r.url} target="_blank" rel="noopener noreferrer">
                          Open form
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="lr-count-line">
              Showing {visible.length} of {rows.length} form{rows.length === 1 ? '' : 's'}
            </p>
          </>
        )}
      </section>

      <section className="panel lr-about">
        <div>
          <h3>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 11v5M12 8h.01" />
            </svg>
            Any carrier's form will do
          </h3>
          <p>
            These link to each carrier's own published form rather than copies, so you always open
            the version they are currently publishing. Any carrier's supplemental is generally fine
            for gathering the information — you do not need the one belonging to the carrier you
            intend to approach.
          </p>
          <p className="lr-about-note">Links last verified {catalog.generatedAt}.</p>
        </div>
        <div>
          <h3>Fill it out, or send it</h3>
          <ul>
            <li>Online forms can be filled in here and come back as a clean PDF</li>
            <li>Or send one to the insured and let them answer the parts only they know</li>
            <li>Carrier PDFs open on the carrier's own site, in a new tab</li>
            <li>Nothing here is submitted to a market — it gathers the information</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
